import { effect, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { forkJoin, from } from 'rxjs';
import { take } from 'rxjs/operators';

import { ShareFileWithSdActions } from './share-file-with-sd.store';
import { StartFlowGlobalStore } from '../../../../start-flow/store/start-flow-global.store';
import { AppState } from '../../../../reducers';
import { IosShareExtensionDataPluginService } from '../services/ios-share-sheet-plugin.service';
import { CoreRoutes } from '../../../../core/routes/core.routes';
import { FilesRoutes } from '../../../../files/routes/files.routes';
import { AndroidSharedFile } from '../services/android-share-sheet-plugin.service';
import { PreferencesService } from '../../biometrics/services/preferences.service';

@Injectable()
export class ShareFileWithSdEffects {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly startFlowStore = inject(StartFlowGlobalStore);
  private readonly iosShareExtensionDataPluginService = inject(
    IosShareExtensionDataPluginService
  );
  private readonly router = inject(Router);
  private readonly preferences = inject(PreferencesService);

  // NOTE: Reacts to StartFlowGlobalStore.complete() signal. When startFlow finishes,
  // we read both iOS (ShareExtensionDataPlugin) and Android (Preferences) shared files
  // and dispatch them to the ShareFileWithSd store. Android Preferences are cleaned up
  // here after consumption; iOS plugin storage is cleared via plugin clear().
  private readonly onStartFlowComplete = effect(() => {
    const isComplete = this.startFlowStore.complete();
    if (!isComplete) return;

    forkJoin([
      from(this.iosShareExtensionDataPluginService.read()),
      this.preferences.getShareFiles(),
    ]).subscribe(([iosShareData, result]) => {
      const shareFiles: AndroidSharedFile[] = Object.values(
        JSON.parse(result?.value ?? '{}')
      );

      if (shareFiles?.length > 0) {
        this.store.dispatch(
          ShareFileWithSdActions.sharedFilesReceived({
            shareFiles,
          })
        );
      }

      this.preferences.removeShareFiles().pipe(take(1)).subscribe();

      if (iosShareData?.length > 0) {
        this.store.dispatch(
          ShareFileWithSdActions.sharedFilesReceived({
            shareFiles: iosShareData.map(sharedFile => ({
              fileName: sharedFile.fileName,
              fileSize: sharedFile.fileSize,
              uri: sharedFile.url,
            })),
          })
        );
        this.iosShareExtensionDataPluginService.clear();
      }

      if (shareFiles?.length > 0 || iosShareData?.length > 0) {
        this.router.navigateByUrl(
          `/${CoreRoutes.root}/${FilesRoutes.shareWithSd}`
        );
      }
    });
  });
}
